/**
 * CustomCallMessage — renders call_log messages as centered, styled call events.
 *
 * Detects messages with `call_log: true` in their custom data and renders them
 * as a centered pill instead of a regular chat bubble.
 *
 * Message types:
 *  - missed_call:   "📞 Missed call from [User Name]"
 *  - call_summary:  "📞 Call Ended · Duration: MM:SS"
 */

// import { useTranslation } from "react-i18next";

interface CallLogData {
  call_log_type: "missed_call" | "call_summary";
  call_type?: "audio" | "video";
  caller_id?: string;
  caller_name?: string;
  call_duration?: number;
  call_duration_formatted?: string;
  participants?: string[];
}

interface CallMessageProps {
  message: {
    id: string;
    text?: string;
    created_at?: string | Date;
    call_log?: boolean;
    call_log_data?: CallLogData;
    [key: string]: any;
  };
}

/**
 * Determines if a message is a call log message.
 */
export const isCallLogMessage = (message: any): boolean => {
  return !!message?.call_log;
};

/**
 * Phone icon SVG for the call log pill
 */
const PhoneIcon = ({ missed }: { missed: boolean }) => (
  <svg
    className={`w-3.5 h-3.5 flex-shrink-0 ${missed ? "text-red-400" : "text-green-500"}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    {missed ? (
      <>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L8.23 3.684A1 1 0 007.28 3H5z"
        />
      </>
    ) : (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    )}
  </svg>
);

/**
 * Format a Date object or ISO string to a localized time string (e.g. "2:30 PM")
 */
const formatTime = (date?: string | Date): string => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const CustomCallMessage = ({ message }: CallMessageProps) => {
  // const { t } = useTranslation();
  const data = message.call_log_data;
  const isMissed = data?.call_log_type === "missed_call";
  const timeStr = formatTime(message.created_at);

  // Build display text
  let displayText = message.text || "";
  if (!displayText && data) {
    if (isMissed) {
      displayText = `Missed call from ${data.caller_name || "Unknown"}`;
    } else {
      displayText = `Call Ended · Duration: ${data.call_duration_formatted || "00:00"}`;
    }
  }

  // Build subtitle with extra details
  const subtitleParts: string[] = [];
  if (data?.call_type) {
    subtitleParts.push(data.call_type === "video" ? "Video" : "Audio");
  }
  if (data?.participants && data.participants.length > 0) {
    subtitleParts.push(data.participants.join(", "));
  }
  const subtitle = subtitleParts.join(" · ");

  return (
    <div className="flex justify-center my-3 px-4">
      <div
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium shadow-sm ${
          isMissed
            ? "bg-red-50 text-red-600 border border-red-100"
            : "bg-gray-50 text-gray-500 border border-gray-100"
        }`}
      >
        <PhoneIcon missed={isMissed} />
        <span>{displayText}</span>
        {timeStr && (
          <>
            <span className="text-gray-300">·</span>
            <span className="text-gray-400">{timeStr}</span>
          </>
        )}
      </div>
      {/* Subtitle with call details (shown below on hover or always if present) */}
      {subtitle && (
        <div className="hidden">
          {/* Accessible tooltip data */}
          <span>{subtitle}</span>
        </div>
      )}
    </div>
  );
};
